import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { RES_STATUS } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CalendarCheck, CircleNotch, Check, X, WhatsappLogo, Users, Clock, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = [["", "Todas"], ["pendente", "Pendentes"], ["confirmada", "Confirmadas"], ["recusada", "Recusadas"]];

function ReservationCard({ r, isNew, onDecide, busy }) {
  const st = RES_STATUS[r.status];
  return (
    <div className={`stagger-item rounded-xl border bg-card p-4 ${isNew ? "border-primary ring-1 ring-primary/30" : "border-border"}`}
      data-testid={`reservation-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate font-semibold">{r.name}</span>
            {isNew && <Badge className="h-5 px-1.5 text-[10px]" data-testid={`reservation-new-${r.id}`}>Nova</Badge>}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><CalendarCheck size={14} />{fmtDate(r.date)}</span>
            <span className="flex items-center gap-1"><Clock size={14} />{r.time}</span>
            <span className="flex items-center gap-1"><Users size={14} />{r.people}</span>
            <a href={`tel:${r.phone}`} className="flex items-center gap-1 hover:text-primary"><Phone size={14} />{r.phone}</a>
          </div>
        </div>
        <Badge variant="outline" className={st?.cls} data-testid={`reservation-status-${r.id}`}>{st?.label}</Badge>
      </div>
      {r.special_request && <p className="mt-2 rounded-lg bg-muted px-3 py-2 text-sm">{r.special_request}</p>}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground">Origem: {r.origin} · {fmtDate(r.created_at)}</span>
        {r.status === "pendente" ? (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full text-destructive" disabled={busy}
              onClick={() => onDecide(r, "reject")} data-testid={`reject-reservation-${r.id}`}><X size={14} className="mr-1" /> Recusar</Button>
            <Button size="sm" className="rounded-full" disabled={busy}
              onClick={() => onDecide(r, "confirm")} data-testid={`confirm-reservation-${r.id}`}><Check size={14} className="mr-1" /> Confirmar</Button>
          </div>
        ) : r.whatsapp_url && (
          <Button size="sm" variant="outline" className="rounded-full" asChild data-testid={`whatsapp-reservation-${r.id}`}>
            <a href={r.whatsapp_url} target="_blank" rel="noreferrer"><WhatsappLogo size={16} className="mr-1 text-emerald-600" /> WhatsApp</a>
          </Button>
        )}
      </div>
    </div>
  );
}

export default function Reservations() {
  const [items, setItems] = useState([]);
  const [newIds, setNewIds] = useState(new Set());
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/site/reservations", { params: { status: status || undefined } });
      setItems(data.items);
      const unseen = data.items.filter((r) => r.seen === false).map((r) => r.id);
      if (unseen.length) {
        setNewIds((prev) => new Set([...prev, ...unseen]));
        await api.post("/site/reservations/mark-seen");
        window.dispatchEvent(new Event("reservations-updated"));
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (r, action) => {
    if (action === "reject" && !window.confirm(`Recusar a reserva de ${r.name}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/site/reservations/${r.id}/${action}`);
      setItems((list) => list.map((x) => (x.id === data.id ? data : x)));
      setResult(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="reservations-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Site público</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Reservas</h1>
      </div>
      <div className="flex gap-1 overflow-x-auto rounded-full bg-muted p-1 sm:w-fit">
        {TABS.map(([v, l]) => (
          <button key={v || "all"} onClick={() => setStatus(v)} data-testid={`reservations-tab-${v || "all"}`}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${status === v ? "bg-card shadow-sm" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      {loading ? (
        <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card py-16 text-center" data-testid="reservations-empty">
          <CalendarCheck size={40} className="text-muted-foreground" />
          <p className="text-muted-foreground">Sem reservas por agora.</p>
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {items.map((r) => <ReservationCard key={r.id} r={r} isNew={newIds.has(r.id)} onDecide={decide} busy={busy} />)}
        </div>
      )}

      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="max-w-sm" data-testid="reservation-result-dialog">
          <DialogHeader>
            <DialogTitle>Reserva {result?.status === "confirmada" ? "confirmada" : "recusada"}</DialogTitle>
            <DialogDescription>Avise {result?.name} por WhatsApp com a mensagem já preparada.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResult(null)}>Fechar</Button>
            {result?.whatsapp_url && (
              <Button asChild className="bg-emerald-600 hover:bg-emerald-700" data-testid="open-whatsapp-btn">
                <a href={result.whatsapp_url} target="_blank" rel="noreferrer" onClick={() => setResult(null)}>
                  <WhatsappLogo size={18} className="mr-1.5" /> Enviar no WhatsApp
                </a>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
